import { useEffect, useRef } from 'react';
import { ActionButton } from './components/ActionButton.jsx';
import { Card, Score } from './components/Card.jsx';
import { Header } from './components/Header.jsx';
import { BottomNav, LanguageSwitch, ModeSwitch, ProductSwitch } from './components/Nav.jsx';
import { PlannerCard } from './components/PlannerCard.jsx';
import { initTelegramApp } from './lib/telegram.js';
import { t } from './lib/i18n.js';
import { useAppStore } from './store.js';
import { CareevaUxSystem } from './components/ux/CareevaUxKit.jsx';

const telegramContext = initTelegramApp();

function TodayView({ data, mode }) {
  return (
    <section className="view">
      <div className="section-head">
        <div>
          <span className="eyebrow">Today cockpit</span>
          <h2>Что требует внимания</h2>
        </div>
        <ActionButton action="refresh_plan" label="Обновить" variant="secondary" />
      </div>
      <section className="priority-stack">
        {data.plannerItems.map((item) => <PlannerCard key={item.id} item={item} />)}
      </section>
      <CalendarPanel calendar={data.calendar} />
      {mode !== 'quick' && (
        <section className="workspace-grid">
          <Card className="panel">
            <h3>AI scoring engine</h3>
            <div className="metric"><span>Next best action</span><strong>{data.plannerScoring?.nextBestAction || '—'}</strong></div>
            <div className="metric"><span>Average day score</span><strong>{data.plannerScoring?.scoreAverage ?? '—'}</strong></div>
            <div className="metric"><span>P1 / P2</span><strong>{data.plannerScoring?.p1Count ?? 0} / {data.plannerScoring?.p2Count ?? 0}</strong></div>
            <div className="metric"><span>Pipeline total</span><strong>{data.pipelineAnalytics?.pipeline_total ?? '—'}</strong></div>
            <div className="metric"><span>Свободные окна</span><strong>{(data.reminderOptions || ['16:00', '17:00', '18:00']).join(', ')}</strong></div>
          </Card>
          <Card className="panel">
            <h3>Calendar autopilot</h3>
            <div className="metric"><span>Интервью 72ч</span><strong>{data.calendar?.interviews?.length ?? 0}</strong></div>
            <div className="metric"><span>Запланировано reminders</span><strong>{data.calendar?.reminders?.length ?? 0}</strong></div>
            <div className="chip-row">
              {(data.reminderOptions?.length ? data.reminderOptions : ['16:00', '17:00', '18:00']).map((time) => <ActionButton key={time} action={`remind_${time.slice(0,2)}`} label={time} variant="secondary" />)}
            </div>
            <div className="chip-row">
              <ActionButton action="open_meeting_link" label="Ссылка" variant="secondary" />
              <ActionButton action="calendar_sync" label="Sync" variant="secondary" />
            </div>
            <p>AI подтверждает встречи за 15 минут и показывает ссылку за 5 минут.</p>
          </Card>
        </section>
      )}
      {mode === 'pro' && (
        <section className="workspace-grid">
          <Card className="panel code-panel">
            <h3>Planner rule</h3>
            <pre>{`score = urgency * 0.4
      + impact * 0.3
      + market_risk * 0.25
      - effort * 0.05

Priority bands:
P1 >= 88
P2 >= 72
P3 >= 55`}</pre>
          </Card>
          <ActionHistory actions={data.actionHistory || []} />
          <DataSourcesPanel dataSources={data.dataSources} />
        </section>
      )}
    </section>
  );
}


function CalendarPanel({ calendar }) {
  const interviews = calendar?.interviews || [];
  const reminders = calendar?.reminders || [];
  const due = calendar?.dueReminders || [];
  return (
    <section className="workspace-grid calendar-grid">
      <Card className="panel">
        <div className="card-head"><div><span className="eyebrow">Calendar</span><h3>Ближайшие интервью</h3></div><ActionButton action="calendar_sync" label="Sync" variant="secondary" /></div>
        {interviews.length === 0 && <p>Интервью на ближайшие 72 часа не найдены.</p>}
        {interviews.slice(0, 5).map((item) => (
          <div className="calendar-row" key={`${item.source}-${item.id}`}>
            <div><strong>{item.time} · {item.title}</strong><span>{item.candidate} · {item.status}</span></div>
            <div className="chip-row compact">
              <ActionButton action="confirm_interview" label="✓" variant="secondary" payload={{ id: item.id, entityType: item.source }} />
              <ActionButton action="reschedule_interview" label="↻" variant="secondary" payload={{ id: item.id, entityType: item.source }} />
              <ActionButton action="open_meeting_link" label="link" variant="secondary" payload={{ id: item.id, entityType: item.source }} />
            </div>
          </div>
        ))}
      </Card>
      <Card className="panel">
        <span className="eyebrow">Reminders</span>
        <h3>Умные напоминания</h3>
        {due.length > 0 && <div className="error-banner">Due now: {due.length}</div>}
        {reminders.length === 0 && <p>Пока нет запланированных reminder events.</p>}
        {reminders.slice(0, 6).map((item) => (
          <div className="reminder-row" key={item.id}>
            <div><strong>{item.time} · {item.title}</strong><span>{item.kind}</span></div>
            <ActionButton action="refresh_plan" label="done" variant="secondary" payload={{ reminderId: item.id }} />
          </div>
        ))}
      </Card>
    </section>
  );
}


function DataSourcesPanel({ dataSources }) {
  const sources = dataSources?.sources || [];
  if (!sources.length) return null;
  const primary = dataSources?.primary?.length ? dataSources.primary.join(', ') : 'fallback';
  return (
    <Card className="panel source-panel">
      <div className="card-head">
        <div>
          <span className="eyebrow">Data adapters</span>
          <h3>Real data sources</h3>
        </div>
        <span className="status-pill">{primary}</span>
      </div>
      <div className="source-list">
        {sources.map((source) => (
          <div className="source-row" key={source.name}>
            <div>
              <strong>{source.name}</strong>
              <span>{source.message || 'ok'}</span>
            </div>
            <div className="source-state">
              <span>{source.rows}</span>
              <span className={`state-pill ${source.available ? 'on' : 'off'}`}>{source.used ? 'USED' : source.available ? 'READY' : 'MISS'}</span>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ActionHistory({ actions }) {
  return (
    <Card className="panel">
      <h3>Action journal</h3>
      {actions.length === 0 && <p>Действий пока нет.</p>}
      {actions.map((item) => (
        <div className="history-row" key={item.id}>
          <strong>{item.label || item.action}</strong>
          <span>{item.message || item.status}</span>
        </div>
      ))}
    </Card>
  );
}

function ListView({ title, eyebrow, items, action }) {
  return (
    <section className="view">
      <div className="section-head"><div><span className="eyebrow">{eyebrow}</span><h2>{title}</h2></div></div>
      <section className="data-list">
        {items.map((item) => (
          <Card key={`${item.id}-${item.pipelineId || ''}`} className="data-card">
            <div><h3>{item.title}</h3><p>{item.meta}</p></div>
            <div className="right-stack"><Score value={item.score} /><ActionButton action={action} label="Открыть" payload={{ id: item.id, pipelineId: item.pipelineId }} /></div>
          </Card>
        ))}
      </section>
    </section>
  );
}

function PipelineView({ pipeline }) {
  return (
    <section className="view">
      <div className="section-head"><div><span className="eyebrow">Smart pipeline</span><h2>Pipeline</h2></div></div>
      <section className="pipeline-board">
        {Object.entries(pipeline).map(([stage, items]) => (
          <Card key={stage} className="stage">
            <h3>{stage}</h3>
            {items.length === 0 && <p className="empty-stage">Пусто</p>}
            {items.map((item) => typeof item === 'string'
              ? <p key={item}>{item}</p>
              : <div className="pipeline-item" key={item.pipelineId || item.id}>
                  <div><strong>{item.title}</strong><span>{item.subtitle}</span></div>
                  <Score value={item.score} />
                </div>)}
          </Card>
        ))}
      </section>
    </section>
  );
}

function AiView({ automations, actionHistory }) {
  return (
    <section className="view">
      <div className="section-head"><div><span className="eyebrow">Automation cockpit</span><h2>AI Agent</h2></div></div>
      <section className="data-list">
        {automations.map((item) => {
          const normalized = Array.isArray(item) ? { title: item[0], text: item[1], action: item[2], enabled: true } : item;
          return (
            <Card key={normalized.action} className="data-card">
              <div><h3>{normalized.title}</h3><p>{normalized.text}</p></div>
              <div className="right-stack"><span className={`state-pill ${normalized.enabled ? 'on' : 'off'}`}>{normalized.enabled ? 'ON' : 'OFF'}</span><ActionButton action={normalized.action} label="Toggle" /></div>
            </Card>
          );
        })}
      </section>
      <section className="workspace-grid"><ActionHistory actions={actionHistory || []} /></section>
    </section>
  );
}



function CandidatePlannerSummary({ planner }) {
  const language = useAppStore((s) => s.language);
  const summary = planner?.summary || {};
  return (
    <Card className="candidate-planner-summary">
      <div>
        <span className="eyebrow">Candidate Daily Planner</span>
        <h3>{summary.nextBestAction || 'Careeva планирует следующий шаг поиска'}</h3>
        <p>План собирается из CV score, job matches, follow-ups, interview mode и salary positioning.</p>
        <div className="meta-row">
          <span>Avg {summary.scoreAverage ?? '—'}</span>
          <span>P1/P2 {summary.p1Count ?? 0}/{summary.p2Count ?? 0}</span>
          <span>Focus {summary.focus || '—'}</span>
        </div>
      </div>
      <div className="right-stack">
        <span className="status-pill">{(summary.freeSlots || ['18:00']).join(' · ')}</span>
        <ActionButton action="career_review_jobs" label={t(language, 'startNow')} />
      </div>
    </Card>
  );
}

function CareerTodayView({ data, mode }) {
  const profile = data.profile || {};
  const analytics = data.careerAnalytics || {};
  const language = useAppStore((s) => s.language);
  return (
    <section className="view career-view">
      <div className="section-head">
        <div>
          <span className="eyebrow">Career OS · {data.intentState || 'passive_search'}</span>
          <h2>{t(language, 'careerPlan')}</h2>
        </div>
        <ActionButton action="career_review_jobs" label={t(language, 'refresh')} variant="secondary" />
      </div>
      <Card className="career-profile-card">
        <div>
          <span className="eyebrow">AI Career Operator</span>
          <h3>{profile.headline || 'Career profile'}</h3>
          <p>{profile.summary}</p>
          <div className="meta-row indicator-row">
            <span title={t(language, 'cvBadge')}>CV {analytics.cvScore ?? profile.cvScore ?? '—'}%</span>
            <span title={t(language, 'marketBadge')}>Market {analytics.marketValue || profile.marketValue || '—'}</span>
            <span title={t(language, 'stealthBadge')}>{profile.visibility || 'Visible'}</span>
          </div>
          <details className="indicator-explainer">
            <summary>{t(language, 'badgesTitle')}</summary>
            <p>{t(language, 'cvBadge')}</p>
            <p>{t(language, 'marketBadge')}</p>
            <p>{t(language, 'stealthBadge')}</p>
          </details>
        </div>
        <div className="right-stack career-intents">
          <ActionButton action="career_set_intent_passive" label="Passive" variant="secondary" />
          <ActionButton action="career_set_intent_active" label="Active" variant="secondary" />
          <ActionButton action="career_set_intent_interview" label="Interview" variant="secondary" />
        </div>
      </Card>
      <CandidatePlannerSummary planner={data.candidateDailyPlanner} />
      <section className="priority-stack">
        {(data.careerFeed || []).map((item) => <div className="planner-wrap" key={item.id}><PlannerCard item={item} /><div className="task-control-row"><ActionButton action="career_snooze_task" label={t(language, 'snooze')} variant="secondary" payload={{ taskId: item.id, hours: 3 }} /><ActionButton action="career_complete_task" label={t(language, 'done')} variant="secondary" payload={{ taskId: item.id }} /></div></div>)}
      </section>
      {mode !== 'quick' && (
        <section className="workspace-grid">
          <Card className="panel">
            <h3>Search preferences</h3>
            <div className="metric"><span>Target roles</span><strong>{(data.preferences?.targetRoles || []).slice(0,2).join(', ') || '—'}</strong></div>
            <div className="metric"><span>Salary floor</span><strong>{data.preferences?.salaryMin ? `${data.preferences.salaryMin} ${data.preferences.currency || ''}` : '—'}</strong></div>
            <div className="metric"><span>Locations</span><strong>{(data.preferences?.locations || []).slice(0,3).join(', ') || '—'}</strong></div>
            <ActionButton action="career_edit_preferences" label="Настроить цели" />
          </Card>
          <Card className="panel">
            <h3>Career analytics</h3>
            <div className="metric"><span>Top matches</span><strong>{analytics.topMatches ?? 0}</strong></div>
            <div className="metric"><span>Saved / Applied</span><strong>{analytics.saved ?? 0} / {analytics.applied ?? 0}</strong></div>
            <div className="metric"><span>Skill gaps</span><strong>{(profile.gaps || []).length}</strong></div>
            <p>{(profile.gaps || []).slice(0, 2).join(' · ')}</p>
          </Card>
        </section>
      )}
    </section>
  );
}

function JobDecisionBadge({ job }) {
  const label = job.recommendation === 'apply_with_cv_adaptation' ? 'Adapt CV + apply'
    : job.recommendation === 'apply' ? 'Apply candidate'
    : job.recommendation === 'review' ? 'Review' : 'Monitor';
  return <span className={`decision-badge ${job.priorityBand || 'P4'}`}>{job.priorityBand || 'P4'} · {label}</span>;
}

function CareerJobsView({ jobs, engine }) {
  const activeJobs = (jobs || []).filter((item) => item.status !== 'ignored');
  const approvalQueue = activeJobs.filter((item) => item.status === 'needs_approval');
  return (
    <section className="view">
      <div className="section-head">
        <div><span className="eyebrow">AI job search engine</span><h2>Вакансии для тебя</h2></div>
        <div className="chip-row compact">
          <ActionButton action="career_run_job_search" label="Run search" variant="secondary" />
          <ActionButton action="career_toggle_auto_apply" label={engine?.autoApplyRules?.enabled ? 'Auto ON' : 'Auto OFF'} variant="secondary" />
        </div>
      </div>
      {approvalQueue.length > 0 && (
        <Card className="approval-banner">
          <div><strong>{approvalQueue.length} отклика ждут approve</strong><span>Careeva подготовила заявки, но не отправит их без подтверждения.</span></div>
          <ActionButton action="career_review_jobs" label="Review queue" />
        </Card>
      )}
      <section className="data-list">
        {activeJobs.map((item) => (
          <Card key={item.id} className="data-card career-job-card">
            <div>
              <div className="job-title-row"><h3>{item.title}</h3><JobDecisionBadge job={item} /></div>
              <p>{item.company} · {item.location} · {item.salary}</p>
              <div className="meta-row">
                <span>{item.status}</span><span>Growth {item.growthScore}%</span><span>Company {item.companyScore}%</span><span>Risk {item.risk}</span>
              </div>
              <div className="score-breakdown">
                <span>match {item.scoreBreakdown?.match ?? item.matchScore}</span>
                <span>growth {item.scoreBreakdown?.growth ?? item.growthScore}</span>
                <span>company {item.scoreBreakdown?.company ?? item.companyScore}</span>
                <span>risk -{item.scoreBreakdown?.riskPenalty ?? 0}</span>
              </div>
              <div className="explain-grid compact-explain">
                <div className="reason-block"><strong>Why matched</strong><ul>{(item.why || []).slice(0,3).map((x) => <li key={x}>{x}</li>)}</ul></div>
                <div className="reason-block"><strong>Risks</strong><ul>{(item.risks || ['No major risks']).map((x) => <li key={x}>{x}</li>)}</ul></div>
              </div>
            </div>
            <div className="right-stack vertical-actions">
              <Score value={item.decisionScore ?? item.matchScore} />
              {item.status === 'needs_approval'
                ? <ActionButton action="career_approve_apply" label="Approve" payload={{ id: item.id, entityType: 'career_job' }} />
                : <ActionButton action="career_queue_apply" label="Queue apply" payload={{ id: item.id, entityType: 'career_job' }} />}
              <ActionButton action="career_adapt_cv_for_job" label="Adapt CV" variant="secondary" payload={{ id: item.id, entityType: 'career_job' }} />
              <ActionButton action="career_save_job" label="Save" variant="secondary" payload={{ id: item.id, entityType: 'career_job' }} />
              <ActionButton action="career_ignore_job" label="Hide" variant="secondary" payload={{ id: item.id, entityType: 'career_job' }} />
            </div>
          </Card>
        ))}
      </section>
    </section>
  );
}

function CareerApplicationsView({ applications }) {
  const grouped = (applications || []).reduce((acc, item) => {
    const key = item.stage || 'saved';
    acc[key] = acc[key] || [];
    acc[key].push(item);
    return acc;
  }, {});
  const stages = ['approval_queue', 'cv_adapt', 'applied', 'follow_up', 'follow_up_sent', 'interview', 'offer'];
  return (
    <section className="view">
      <div className="section-head"><div><span className="eyebrow">Job search pipeline</span><h2>Applications</h2></div></div>
      <section className="pipeline-board career-pipeline">
        {stages.map((stage) => (
          <Card key={stage} className="stage">
            <h3>{stage.replaceAll('_', ' ')}</h3>
            {!(grouped[stage] || []).length && <p className="empty-stage">Пусто</p>}
            {(grouped[stage] || []).map((item) => (
              <div className="pipeline-item" key={item.id}>
                <div><strong>{item.title}</strong><span>{item.company} · {item.status}</span><small>{item.notes}</small></div>
                <div className="right-stack compact">
                  <Score value={item.matchScore} />
                  {stage === 'approval_queue' && <ActionButton action="career_approve_apply" label="OK" variant="secondary" payload={{ id: item.jobId, entityType: 'career_job' }} />}
                </div>
              </div>
            ))}
          </Card>
        ))}
      </section>
    </section>
  );
}


function CareerCvManagementView({ data }) {
  const cv = data.cvManagement || {};
  const active = cv.activeCv;
  const uploadCvFile = useAppStore((state) => state.uploadCvFile);
  const fileRef = useRef(null);
  const onFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await uploadCvFile(file);
    event.target.value = '';
  };
  return (
    <section className="view cv-management-view">
      <div className="section-head">
        <div>
          <span className="eyebrow">Career OS · CV Management</span>
          <h2>CV workspace</h2>
          <p>Upload, parse, activate, delete and optimize CV versions. This is the source of truth for job matching and applications.</p>
        </div>
        <div className="chip-row compact">
          <input ref={fileRef} className="hidden-file-input" type="file" accept=".txt,.md,.pdf,.doc,.docx,.rtf" onChange={onFileChange} />
          <button onClick={() => fileRef.current?.click()}>Upload CV</button>
          <ActionButton action="career_cv_import_uploads" label="Import uploads" variant="secondary" />
        </div>
      </div>

      {!active && (
        <Card className="panel cv-empty-state wide-panel">
          <span className="eyebrow">Start here</span>
          <h3>No active CV yet</h3>
          <p>Upload a resume or import files from the server-side <code>uploads</code> folder. Careeva will parse the profile, extract skills, calculate CV score and unlock CV optimization, job matching and apply flows.</p>
          <div className="chip-row">
            <button onClick={() => fileRef.current?.click()}>Upload first CV</button>
            <ActionButton action="career_cv_import_uploads" label="Import 7 resumes from uploads" variant="secondary" />
          </div>
        </Card>
      )}

      {active && (
        <Card className="panel cv-active-card wide-panel">
          <div className="card-head">
            <div>
              <span className="eyebrow">Active CV</span>
              <h3>{active.filename}</h3>
              <p>{active.parsed?.summary || 'Parsed profile ready.'}</p>
            </div>
            <Score value={active.score || active.parsed?.cvScore || 0} />
          </div>
          <div className="metric-grid mini-metrics">
            <div className="metric"><span>Headline</span><strong>{active.headline}</strong></div>
            <div className="metric"><span>Seniority</span><strong>{active.parsed?.seniority || '—'}</strong></div>
            <div className="metric"><span>Parser confidence</span><strong>{Math.round((active.parsed?.confidence || 0) * 100)}%</strong></div>
          </div>
          <div className="chip-row">{(active.skills || []).slice(0, 12).map((skill) => <span className="chip" key={skill}>{skill}</span>)}</div>
          <div className="explain-grid compact-explain">
            <div className="reason-block"><strong>Parsed gaps</strong><ul>{(active.gaps || ['No major gaps']).map((gap) => <li key={gap}>{gap}</li>)}</ul></div>
            <div className="reason-block"><strong>What this CV powers</strong><ul><li>Job match scoring</li><li>CV optimizer drafts</li><li>Auto-apply approvals</li><li>Candidate Daily Planner</li></ul></div>
          </div>
          <div className="chip-row">
            <ActionButton action="career_cv_optimize" label="Optimize active CV" payload={{ cvId: active.id }} />
            <ActionButton action="career_run_job_search" label="Find jobs" variant="secondary" />
            <ActionButton action="career_cv_delete" label="Delete CV" variant="secondary" payload={{ cvId: active.id }} />
          </div>
        </Card>
      )}

      <section className="workspace-grid cv-workspace-grid">
        <Card className="panel">
          <div className="card-head"><div><span className="eyebrow">Documents</span><h3>Uploaded CV files</h3></div><span className="status-pill">{(cv.documents || []).length} files</span></div>
          {(cv.documents || []).length === 0 && <p>No CV documents yet.</p>}
          {(cv.documents || []).map((doc) => (
            <div className="cv-document-row" key={doc.id}>
              <div><strong>{doc.filename}</strong><span>{doc.isActive ? 'Active · ' : ''}score {doc.score}% · {doc.status}</span></div>
              <div className="chip-row compact">
                {!doc.isActive && <ActionButton action="career_cv_activate" label="Use" variant="secondary" payload={{ cvId: doc.id }} />}
                <ActionButton action="career_cv_optimize" label="Draft" variant="secondary" payload={{ cvId: doc.id }} />
                <ActionButton action="career_cv_delete" label="Delete" variant="secondary" payload={{ cvId: doc.id }} />
              </div>
            </div>
          ))}
        </Card>
        <Card className="panel">
          <div className="card-head"><div><span className="eyebrow">Versions</span><h3>CV drafts</h3></div><span className="status-pill">{(cv.versions || []).length}</span></div>
          {(cv.versions || []).length === 0 && <p>No versions yet. Upload a CV and create an optimized draft.</p>}
          {(cv.versions || []).slice(0, 8).map((version) => (
            <div className="cv-version" key={version.id}>
              <strong>{version.title}</strong>
              <p>{version.summary}</p>
              <div className="meta-row"><span>{version.targetRole || 'general'}</span><span>score {version.score}%</span>{version.isActive && <span>active</span>}</div>
            </div>
          ))}
        </Card>
        <Card className="panel wide-panel">
          <div className="card-head"><div><span className="eyebrow">Parsing history</span><h3>CV events</h3></div></div>
          {(cv.events || []).length === 0 && <p>No CV events yet.</p>}
          {(cv.events || []).map((event) => <div className="history-row" key={event.id}><strong>{event.action}</strong><span>{event.message} · {event.created_at}</span></div>)}
        </Card>
      </section>
      <CareerOptimizerPanel optimizer={data.optimizer} />
    </section>
  );
}

function CareerProfileView({ data }) {
  const profile = data.profile || {};
  return (
    <section className="view">
      <div className="section-head"><div><span className="eyebrow">Candidate profile</span><h2>Profile & preferences</h2></div></div>
      <section className="workspace-grid">
        <Card className="panel">
          <h3>{profile.headline}</h3>
          <p>{profile.summary}</p>
          <div className="chip-row">{(profile.skills || []).map((skill) => <span className="chip" key={skill}>{skill}</span>)}</div>
        </Card>
        <Card className="panel">
          <h3>Search intent</h3>
          <div className="metric"><span>State</span><strong>{data.intentState}</strong></div>
          <div className="metric"><span>Remote</span><strong>{data.preferences?.remote}</strong></div>
          <div className="metric"><span>Avoid</span><strong>{(data.preferences?.avoid || []).join(', ')}</strong></div>
        </Card>
      </section>
      <CareerOptimizerPanel optimizer={data.optimizer} />
    </section>
  );
}

function CareerOptimizerPanel({ optimizer }) {
  const analysis = optimizer?.analysis || {};
  const versions = optimizer?.cvVersions || [];
  const history = optimizer?.history || [];
  return (
    <section className="workspace-grid optimizer-grid">
      <Card className="panel optimizer-hero">
        <div className="card-head">
          <div><span className="eyebrow">CV/Profile Optimizer</span><h3>Profile strength</h3></div>
          <Score value={analysis.profileScore ?? analysis.currentCvScore ?? 0} />
        </div>
        <p>{analysis.salaryPositioning?.text || 'AI анализирует позиционирование, пробелы и готовность CV к откликам.'}</p>
        <div className="metric"><span>Current CV</span><strong>{analysis.currentCvScore ?? '—'}%</strong></div>
        <div className="metric"><span>Target</span><strong>{analysis.targetCvScore ?? 88}%</strong></div>
        <div className="metric"><span>Uplift potential</span><strong>+{analysis.upliftPotential ?? 0}%</strong></div>
        <div className="chip-row">
          <ActionButton action="career_optimizer_analyze" label="Analyze" />
          <ActionButton action="career_optimizer_adapt_cv" label="Create CV draft" variant="secondary" />
          <ActionButton action="career_optimizer_interview_prep" label="Interview prep" variant="secondary" />
        </div>
      </Card>
      <Card className="panel">
        <h3>Skill coverage</h3>
        {Object.entries(analysis.coverage || {}).map(([key, value]) => (
          <div className="coverage-row" key={key}><span>{key.replaceAll('_', ' ')}</span><strong>{value}%</strong></div>
        ))}
      </Card>
      <Card className="panel">
        <h3>Gap analysis</h3>
        {!(analysis.gaps || []).length && <p>No major gaps detected.</p>}
        {(analysis.gaps || []).slice(0, 4).map((gap) => (
          <div className="gap-row" key={gap.area}><strong>{gap.area} · {gap.score}%</strong><span>{gap.recommendation}</span></div>
        ))}
      </Card>
      <Card className="panel">
        <h3>Improvement roadmap</h3>
        {(analysis.roadmap || []).map((step) => (
          <div className="history-row" key={step.step}><strong>{step.step}. {step.title}</strong><span>{step.text}</span></div>
        ))}
      </Card>
      <Card className="panel wide-panel">
        <div className="card-head"><div><span className="eyebrow">Drafts</span><h3>CV versions & cover messages</h3></div><ActionButton action="career_optimizer_cover_letter" label="Cover message" variant="secondary" /></div>
        {versions.length === 0 && <p>Пока нет сохранённых CV drafts. Нажми “Create CV draft”.</p>}
        {versions.slice(0, 3).map((item) => (
          <div className="cv-version" key={item.id}>
            <strong>{item.title}</strong>
            <p>{item.summary}</p>
            <div className="chip-row">{(item.skills || []).slice(0, 6).map((skill) => <span className="chip" key={skill}>{skill}</span>)}</div>
            {item.coverMessage && <small>{item.coverMessage}</small>}
          </div>
        ))}
      </Card>
      <Card className="panel">
        <h3>Optimizer history</h3>
        {history.length === 0 && <p>История оптимизаций пока пустая.</p>}
        {history.slice(0, 5).map((item) => <div className="history-row" key={item.id}><strong>{item.action}</strong><span>score {item.profileScore} · {item.createdAt}</span></div>)}
      </Card>
    </section>
  );
}


function AiOrchestrationPanel({ aiStatus, aiProduction, aiResult, data }) {
  const { runAiTask, enqueueAiJob, refreshAiStatus, aiQueuedJob } = useAppStore();
  const profile = data?.profile || {};
  const job = (data?.jobMatches || [])[0] || {};
  const payload = { profile, job, applications: data?.applications || [], plannerItems: data?.careerFeed || [] };
  const queue = aiProduction?.queue || [];
  const cache = aiProduction?.cache || {};
  return (
    <Card className="panel ai-orchestration-panel">
      <div className="card-head">
        <div><span className="eyebrow">Production AI system</span><h3>{aiStatus?.enabled ? `${aiStatus.provider} · ${aiStatus.model}` : 'Safe fallback mode'}</h3></div>
        <span className={`state-pill ${aiStatus?.enabled ? 'on' : 'off'}`}>{aiStatus?.enabled ? 'LLM ON' : 'FALLBACK'}</span>
      </div>
      <p>Stage 22: background jobs, response cache, provider health, retries, telemetry and local-first routing.</p>
      <div className="metric-grid mini-metrics">
        <div className="metric"><span>Provider health</span><strong>{aiProduction?.providerHealth?.status || 'unknown'}</strong></div>
        <div className="metric"><span>Cache</span><strong>{cache.count || 0} / {cache.hits || 0} hits</strong></div>
        <div className="metric"><span>Queue</span><strong>{queue.map((q) => `${q.status}:${q.count}`).join(' · ') || 'empty'}</strong></div>
      </div>
      <div className="chip-row">
        <button className="secondary" onClick={() => runAiTask('career_explain_match', payload)}>Run now</button>
        <button className="secondary" onClick={() => enqueueAiJob('career_cover_message', payload, 40)}>Queue cover</button>
        <button className="secondary" onClick={() => enqueueAiJob('career_interview_prep', payload, 45)}>Queue prep</button>
        <button className="btn secondary" onClick={refreshAiStatus}>Refresh</button>
      </div>
      {aiQueuedJob && <div className="ai-result-box"><strong>Queued job</strong><pre>{JSON.stringify(aiQueuedJob, null, 2)}</pre></div>}
      {aiResult?.data && (
        <div className="ai-result-box">
          <div className="meta-row"><span>trace {aiResult.traceId}</span><span>{aiResult.provider}</span><span>{aiResult.latencyMs}ms</span></div>
          <strong>{aiResult.data.summary || aiResult.data.next_best_action || aiResult.task}</strong>
          <pre>{JSON.stringify(aiResult.data, null, 2)}</pre>
        </div>
      )}
      {(aiProduction?.recentJobs || []).length > 0 && (
        <div className="source-list compact-source-list">
          {aiProduction.recentJobs.slice(0, 5).map((job) => (
            <div className="source-row" key={job.job_id}>
              <div><strong>{job.task}</strong><span>{job.job_id}</span></div>
              <div className="source-state"><span>{job.attempts} tries</span><span className={`state-pill ${job.status === 'done' ? 'on' : 'off'}`}>{job.status}</span></div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function CareerAiView({ data, aiStatus, aiProduction, aiResult }) {
  const engine = data.jobSearchEngine || {};
  const rules = engine.autoApplyRules || {};
  return (
    <section className="view">
      <div className="section-head"><div><span className="eyebrow">Career automation</span><h2>AI Career Agent</h2></div></div>
      <section className="workspace-grid">
        <Card className="panel">
          <h3>Job search rules</h3>
          <div className="metric"><span>Auto-apply</span><strong>{rules.enabled ? 'Enabled' : 'Disabled'}</strong></div>
          <div className="metric"><span>Min score</span><strong>{rules.minScore ?? 92}</strong></div>
          <div className="metric"><span>Approve before apply</span><strong>{rules.approveBeforeApply ? 'Yes' : 'No'}</strong></div>
          <div className="metric"><span>Daily cap</span><strong>{rules.maxApplicationsPerDay ?? 5}</strong></div>
          <div className="chip-row">
            <ActionButton action="career_toggle_auto_apply" label={rules.enabled ? 'Pause auto-apply' : 'Enable auto-apply'} />
            <ActionButton action="career_run_job_search" label="Run search" variant="secondary" />
          </div>
        </Card>
        <Card className="panel code-panel">
          <h3>Scoring formula</h3>
          <pre>{engine.scoreFormula || '0.56*match + 0.24*growth + 0.14*company - risk'}</pre>
          <p>Decision bands: P1 apply, P2 review, P3 save/monitor, P4 skip.</p>
        </Card>
        <ActionHistory actions={data.actionHistory || []} />
        <AiOrchestrationPanel aiStatus={aiStatus} aiProduction={aiProduction} aiResult={aiResult} data={data} />
      </section>
    </section>
  );
}


function CommunicationCenter({ chatData }) {
  const { runChatAction } = useAppStore();
  const threads = chatData?.threads || [];
  const activeId = chatData?.activeThreadId;
  const active = threads.find((item) => item.id === activeId) || threads[0] || {};
  const messages = chatData?.messages || [];
  const trust = chatData?.trustSummary || {};
  const summary = chatData?.summary || {};
  return (
    <section className="view communication-view">
      <div className="section-head">
        <div><span className="eyebrow">Communication Center</span><h2>Рекрутер ↔ Кандидат</h2></div>
        <div className="chip-row compact"><span className="status-pill">Unread {summary.unread || 0}</span><span className="status-pill danger-soft">Overdue {summary.overdue || 0}</span></div>
      </div>
      <section className="workspace-grid communication-grid">
        <Card className="panel thread-list-panel">
          <div className="card-head"><div><span className="eyebrow">Threads</span><h3>Диалоги</h3></div><span className="state-pill on">trust-aware</span></div>
          {threads.length === 0 && <p>Диалогов пока нет.</p>}
          {threads.map((thread) => (
            <button className={`thread-row ${thread.id === active.id ? 'active' : ''}`} key={thread.id} onClick={() => runChatAction({ action: 'chat_open', threadId: thread.id })}>
              <div><strong>{thread.candidate_name}</strong><span>{thread.role} · {thread.company}</span><small>{thread.statusLabel} · ghosting {thread.ghostingRisk}</small></div>
              <div className="right-stack compact"><span className={`decision-badge ${thread.priority}`}>{thread.priority}</span>{thread.overdue && <span className="state-pill off">overdue</span>}</div>
            </button>
          ))}
        </Card>
        <Card className="panel chat-panel wide-panel">
          <div className="card-head"><div><span className="eyebrow">Active conversation</span><h3>{active.candidate_name || 'Диалог'}</h3></div><button className="btn secondary" onClick={() => runChatAction({ action: 'chat_generate_summary', threadId: active.id })}>AI summary</button></div>
          <p className="ai-summary">{active.ai_summary || 'AI summary появится после первых сообщений.'}</p>
          <div className="chat-stream">
            {messages.map((message) => (
              <div className={`message-bubble ${message.sender_role}`} key={message.id}>
                <strong>{message.sender_name}</strong>
                <p>{message.body}</p>
                <span>{message.created_at}</span>
              </div>
            ))}
          </div>
          <div className="quick-replies">
            {(active.quickReplies || []).map((reply) => <button key={reply.text} className="secondary" onClick={() => runChatAction({ action: 'chat_quick_reply', threadId: active.id, text: reply.text })}>{reply.text}</button>)}
          </div>
          <div className="chip-row">
            <button className="btn" onClick={() => runChatAction({ action: 'chat_send_message', threadId: active.id, text: 'Спасибо! Вернусь с деталями и следующим шагом сегодня.' })}>Send safe reply</button>
            <button className="btn secondary" onClick={() => runChatAction({ action: 'chat_schedule_followup', threadId: active.id, hours: 24 })}>Follow-up 24h</button>
          </div>
        </Card>
        <Card className="panel trust-panel">
          <span className="eyebrow">Trust & reputation signals</span>
          <h3>Рейтинг общения</h3>
          <div className="metric"><span>Recruiter trust</span><strong>{trust.recruiterTrust ?? '—'}%</strong></div>
          <div className="metric"><span>Candidate trust</span><strong>{trust.candidateTrust ?? '—'}%</strong></div>
          <div className="metric"><span>Company trust</span><strong>{trust.companyTrust ?? '—'}%</strong></div>
          <p>Рейтинг строится из скорости ответа, follow-up качества, переносов интервью и ghosting-сигналов.</p>
        </Card>
      </section>
    </section>
  );
}

function TrustReputationPanel({ trustData }) {
  const summary = trustData?.summary || {};
  const scores = trustData?.scores || [];
  return (
    <Card className="panel trust-reputation-panel">
      <div className="card-head"><div><span className="eyebrow">Trust graph</span><h3>Reputation System</h3></div><span className="state-pill on">explainable</span></div>
      <div className="metric-grid mini-metrics">
        <div className="metric"><span>Candidate</span><strong>{summary.candidateTrust ?? '—'}%</strong></div>
        <div className="metric"><span>Recruiter</span><strong>{summary.recruiterTrust ?? '—'}%</strong></div>
        <div className="metric"><span>Company</span><strong>{summary.companyTrust ?? '—'}%</strong></div>
      </div>
      <div className="source-list compact-source-list">
        {scores.map((score) => (
          <div className="source-row" key={`${score.entityType}-${score.entityId}`}>
            <div><strong>{score.entityType} · {score.tier}</strong><span>{score.explanation}</span><small>{score.nextBestAction}</small></div>
            <div className="source-state"><span>{score.trustScore}%</span><span className={`state-pill ${score.ghostingRisk < 45 ? 'on' : 'off'}`}>ghost {score.ghostingRisk}</span></div>
          </div>
        ))}
      </div>
      <p>Trust score не блокирует людей автоматически: он меняет friction, reminders и уровень manual approve.</p>
    </Card>
  );
}

function AiQualityPanel({ aiQuality }) {
  const dimensions = aiQuality?.dimensions || {};
  return (
    <Card className="panel ai-quality-panel">
      <div className="card-head"><div><span className="eyebrow">AI Quality</span><h3>{aiQuality?.verdict || 'quality monitor'}</h3></div><Score value={aiQuality?.qualityScore || 0} /></div>
      <div className="metric-grid mini-metrics">
        <div className="metric"><span>Groundedness</span><strong>{dimensions.groundedness ?? '—'}</strong></div>
        <div className="metric"><span>Usefulness</span><strong>{dimensions.usefulness ?? '—'}</strong></div>
        <div className="metric"><span>Safety</span><strong>{dimensions.safety ?? '—'}</strong></div>
        <div className="metric"><span>Latency</span><strong>{dimensions.latency ?? '—'}</strong></div>
      </div>
      <ul className="quality-list">
        {(aiQuality?.recommendations || []).map((item) => <li key={item}>{item}</li>)}
      </ul>
      <p>{aiQuality?.notes || 'Careeva keeps deterministic fallback enabled for critical actions.'}</p>
    </Card>
  );
}

function CommandCenterPanel({ commandCenter, commandResult }) {
  const { runCommand, refreshQualityAndTrust } = useAppStore();
  const suggestions = commandCenter?.suggestions || [];
  const history = commandCenter?.history || [];
  const submit = (text) => runCommand(text);
  return (
    <Card className="panel command-center-panel wide-panel">
      <div className="card-head"><div><span className="eyebrow">AI Command Center</span><h3>Ask Careeva anything</h3></div><button className="btn secondary" onClick={refreshQualityAndTrust}>Refresh</button></div>
      <div className="command-box">
        <input placeholder="Например: найди remote AI вакансии с match > 90" onKeyDown={(event) => { if (event.key === 'Enter' && event.currentTarget.value.trim()) { submit(event.currentTarget.value.trim()); event.currentTarget.value = ''; } }} />
        <button className="btn" onClick={() => submit('Покажи trust score рекрутера и компании')}>Run</button>
      </div>
      <div className="chip-row command-suggestions">
        {suggestions.slice(0, 5).map((item) => <button className="secondary" key={item} onClick={() => submit(item)}>{item}</button>)}
      </div>
      {commandResult && <div className="ai-result-box"><strong>{commandResult.message || commandResult.intent?.intent || 'Command result'}</strong><pre>{JSON.stringify(commandResult, null, 2)}</pre></div>}
      {history.length > 0 && <div className="source-list compact-source-list">{history.slice(0, 5).map((item) => <div className="source-row" key={item.id}><div><strong>{item.command}</strong><span>{item.intent} · {item.status}</span></div><span>{item.created_at}</span></div>)}</div>}
    </Card>
  );
}

function DevResetPanel() {
  const { resetAllState, actionPending } = useAppStore();
  const doReset = () => {
    const ok = window.confirm('Reset Career OS and Hire OS state? Uploads folder will be preserved.');
    if (ok) resetAllState(false);
  };
  const doFullReset = () => {
    const ok = window.confirm('Full reset also clears local user/recruiter identity rows. Continue?');
    if (ok) resetAllState(true);
  };
  return (
    <Card className="panel dev-reset-panel">
      <div className="card-head">
        <div><span className="eyebrow">QA / E2E reset</span><h3>Fresh workspace reset</h3></div>
        <span className="state-pill off">dev only</span>
      </div>
      <p>Clears attached CVs, active CV, jobs, applications, chats, trust events, AI queues, reminders, Hire OS pipeline and action history. Source files in <code>uploads</code> are preserved for repeat testing.</p>
      <div className="chip-row">
        <button className="btn danger" disabled={actionPending === 'dev_reset'} onClick={doReset}>Reset workspace</button>
        <button className="btn secondary" disabled={actionPending === 'dev_reset'} onClick={doFullReset}>Full reset</button>
      </div>
    </Card>
  );
}

export default function App() {
  const { data, careerData, chatData, trustData, aiQuality, commandCenter, commandResult, loading, careerLoading, productMode, mode, view, toast, apiSource, lastError, aiStatus, aiProduction, aiResult, actionPending, loadToday, loadCareer } = useAppStore();

  useEffect(() => { loadToday(); loadCareer(); }, [loadToday, loadCareer]);

  const activeLoading = productMode === 'career' ? careerLoading || !careerData : loading || !data;
  if (activeLoading) {
    return <main className="app-shell"><div className="loading-card glass">Careeva анализирует цели, рынок, pipeline и дедлайны…</div></main>;
  }

  const apiLabel = apiSource === 'mock' ? 'Mock fallback' : productMode === 'career' ? 'API connected · Career OS' : `API connected · ${(data.dataSources?.primary || ['real data']).join(', ')}`;

  return (
    <main className={`app-shell ${productMode === 'career' ? 'career-shell' : ''}`}>
      <Header user={telegramContext.user} />
      <div className="runtime-row product-row"><ProductSwitch /><LanguageSwitch /><ModeSwitch /><span className={`api-pill ${apiSource === 'mock' ? 'mock' : ''}`}>{apiLabel}</span></div>
      <CareevaUxSystem productMode={productMode} aiStatus={aiStatus} aiProduction={aiProduction} lastError={lastError} actionPending={actionPending} apiSource={apiSource} commandCenter={commandCenter} />
      {lastError && <div className="error-banner">{lastError}</div>}
      {productMode === 'recruiter' && view === 'today' && <TodayView data={data} mode={mode} />}
      {productMode === 'recruiter' && view === 'jobs' && <ListView eyebrow="Vacancy control" title="Вакансии" items={data.jobs} action="job_open" />}
      {productMode === 'recruiter' && view === 'candidates' && <ListView eyebrow="Candidate review" title="Кандидаты" items={data.candidates} action="candidate_open" />}
      {productMode === 'recruiter' && view === 'pipeline' && <PipelineView pipeline={data.pipeline} />}
      {productMode === 'recruiter' && view === 'chat' && <CommunicationCenter chatData={chatData} />}
      {productMode === 'recruiter' && view === 'ai' && <><AiView automations={data.automations} actionHistory={data.actionHistory} /><section className="workspace-grid"><CommandCenterPanel commandCenter={commandCenter} commandResult={commandResult} /><AiQualityPanel aiQuality={aiQuality} /><TrustReputationPanel trustData={trustData} /><DevResetPanel /></section></>}

      {productMode === 'career' && view === 'today' && <CareerTodayView data={careerData} mode={mode} />}
      {productMode === 'career' && view === 'jobs' && <CareerJobsView jobs={careerData.jobMatches} engine={careerData.jobSearchEngine} />}
      {productMode === 'career' && view === 'pipeline' && <CareerApplicationsView applications={careerData.applications} />}
      {productMode === 'career' && view === 'cv' && <CareerCvManagementView data={careerData} />}
      {productMode === 'career' && view === 'profile' && <CareerProfileView data={careerData} />}
      {productMode === 'career' && view === 'chat' && <CommunicationCenter chatData={chatData} />}
      {productMode === 'career' && view === 'ai' && <><CareerAiView data={careerData} aiStatus={aiStatus} aiProduction={aiProduction} aiResult={aiResult} /><section className="workspace-grid"><CommandCenterPanel commandCenter={commandCenter} commandResult={commandResult} /><AiQualityPanel aiQuality={aiQuality} /><TrustReputationPanel trustData={trustData} /><DevResetPanel /></section></>}
      <BottomNav />
      <div className="toast" hidden={!toast}>{toast}</div>
    </main>
  );
}
